<template>
  <ElForm label-position="top" require-asterisk-position="right">
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-4 md:flex-row justify-between">
        <ElFormItem
          label="Straße"
          for="Straße"
          class="w-full"
          :error="validationErrors.street?.[0]"
        >
          <ElInput
            id="Straße"
            v-model="form.street"
            @focus="resetError('street')"
          />
        </ElFormItem>
        <ElFormItem
          label="Hausnummer"
          for="Hausnummer"
          class="w-full md:w-52"
          :error="validationErrors.houseNumber?.[0]"
        >
          <ElInput
            id="Hausnummer"
            v-model="form.houseNumber"
            @focus="resetError('houseNumber')"
          />
        </ElFormItem>
      </div>
      <div class="flex flex-col gap-4 md:flex-row justify-between">
        <ElFormItem
          label="Postleitzahl"
          for="Postleitzahl"
          class="w-full md:w-52"
          :error="validationErrors.zipCode?.[0]"
        >
          <ElInput
            id="Postleitzahl"
            v-model="form.zipCode"
            v-maska="'#####'"
            @focus="resetError('zipCode')"
          />
        </ElFormItem>
        <ElFormItem
          label="Stadt"
          for="Stadt"
          class="w-full"
          :error="validationErrors.city?.[0]"
        >
          <ElInput
            id="Stadt"
            v-model="form.city"
            @focus="resetError('city')"
          />
        </ElFormItem>
      </div>
      <ElFormItem
        label="Telefonnummer"
        for="Telefonnummer"
        :error="validationErrors.phone?.[0]"
      >
        <ElInput
          id="Telefonnummer"
          v-model="form.phone"
          @focus="resetError('phone')"
        />
      </ElFormItem>
      <ElFormItem
        label="Mobilfunknummer"
        for="Mobilfunknummer"
        :error="validationErrors.mobile?.[0]"
      >
        <ElInput
          id="Mobilfunknummer"
          v-model="form.mobile"
          @focus="resetError('mobile')"
        />
      </ElFormItem>

      <ElFormItem
        label="E-Mail Adresse"
        for="E-Mail Adresse"
        :error="validationErrors.email?.[0]"
      >
        <ElInput
          id="E-Mail Adresse"
          v-model="form.email"
          placeholder="Ihre E-Mail-Adresse"
          type="email"
          required
          @focus="resetError('email')"
        >
          <template #prefix>
            <FaIcon name="envelope" />
          </template>
        </ElInput>
      </ElFormItem>
    </div>

    <div class="text-right mt-10">
      <AppButton
        type="primary"
        class="w-full sm:w-auto"
        :handler="sendForm"
      >
        Änderung speichern
      </AppButton>
    </div>
  </ElForm>
</template>

<script setup lang="ts">
import AppButton from '@/application/components/ui/AppButton.vue';
import FaIcon from '@/application/components/ui/FaIcon.vue';
import { ElForm, ElFormItem, ElInput } from 'element-plus';
import { vMaska } from 'maska/vue';

import { updateContactData } from '@/api/user/userApi';
import type { UserContactDataRequestPayload } from '@/api/user/types';
import { useApiWithFormData } from '@/application/composables/apiWithFormData';
import { useFormHelper } from '@/application/composables/formHelper';
import { ConversionFunction, convertValues } from '@/application/utils/form/convertValues';
import { TrackEventCategory } from "@/application/utils/tracking/events";
import { storeToRefs } from "pinia";
import { useUserDataStore } from "@/user/store/userDataStore";
import { watch } from "vue";
import { useTagManager } from '@demv_systems/feu-tag-manager';

const { userData } = storeToRefs(useUserDataStore());

const {
  form,
  getMutated,
} = useFormHelper<UserContactDataRequestPayload>({
  email: undefined,
  street: undefined,
  houseNumber: undefined,
  zipCode: undefined,
  city: undefined,
  phone: undefined,
  mobile: undefined,
});

watch(userData, () => {
    form.email = userData.value?.pwEmail ?? undefined;
    form.street = userData.value?.mainAddress?.street ?? undefined;
    form.houseNumber = userData.value?.mainAddress?.houseNumber ?? undefined;
    form.zipCode = userData.value?.mainAddress?.zipCode ?? undefined;
    form.city = userData.value?.mainAddress?.city ?? undefined;
    form.phone = userData.value?.phone ?? undefined;
    form.mobile = userData.value?.mobile ?? undefined;
}, { immediate: true });

const { submit, validationErrors, resetError } = useApiWithFormData(
  updateContactData,
  'Ihre Daten wurden erfolgreich geändert.',
  () => {
    userData.value = {
      pwEmail: form.email ?? null,
      mainAddress: {
        street: form.street ?? null,
        houseNumber: form.houseNumber ?? null,
        zipCode: form.zipCode ?? null,
        city: form.city ?? null,
      },
      phone: form.phone ?? null,
      mobile: form.mobile ?? null,
    };
  }
);

const sendForm = async () => {
  useTagManager().push({ event: TrackEventCategory.CONTACT_DATA, action: 'speichern' })
  await submit(
    convertValues(getMutated(), { zipCode: ConversionFunction.StringToNumberOrNull }) as UserContactDataRequestPayload,
  );
}
</script>
